import { Injectable, TemplateRef } from '@angular/core';
import { TOAST_DEFAULT_TIMEOUT } from '@app/app.constants';
import { Translatable } from '@app/translate/models/translatable';
import { BootstrapSemanticVariation } from '@app/utils/bootstrap';
import { ID } from '@app/utils/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private DEFAULT_TIMEOUT: number = TOAST_DEFAULT_TIMEOUT;
  /**
   * Allow disabling of all toasts.
   */
  private enabled: boolean = true;

  private _toasts$ = new BehaviorSubject<ActiveToast[]>([]);

  get toasts$(): Observable<ActiveToast[]> {
    return this._toasts$.asObservable();
  }

  /**
   * Hide all toasts.
   */
  clear(force: boolean = false): void {
    this._toasts$.pipe(first()).subscribe((toasts) => {
      const toastsThatShouldStay = force ? [] : toasts.filter((toast) => toast.stay);

      this._toasts$.next(toastsThatShouldStay);
    });
  }

  /**
   * Clear all toasts that were created with `stay: true`.
   */
  clearFixed(): void {
    this._toasts$.pipe(first()).subscribe((toasts) => {
      const toastsThatShouldStay = toasts.filter((toast) => toast.stay !== true);

      this._toasts$.next(toastsThatShouldStay);
    });
  }

  /**
   * Show a "danger" toast.
   * @param body String or template reference that is shown as the main message.
   * @param header Optional string or template reference that is shown as the header.
   */
  danger(body: string | TemplateRef<any>, header?: string | TemplateRef<any>): ActiveToast {
    return this.show({
      body,
      header,
      type: 'danger',
    });
  }

  /**
   * Disable the toast service (no toast will be shown until it is enabled again).
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * Enable the toast service.
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Show a "info" toast.
   * @param body String or template reference that is shown as the main message.
   * @param header Optional string or template reference that is shown as the header.
   */
  info(body: string | TemplateRef<any>, header?: string | TemplateRef<any>): ActiveToast {
    return this.show({
      body,
      header,
      type: 'info',
    });
  }

  /**
   * Remove the toast matching the given id.
   * @param id ID of the toast to remove.
   */
  remove(id: string): void {
    this.toasts$.pipe(first()).subscribe((toasts) => {
      this._toasts$.next(toasts.filter((t) => t.id !== id));
    });
  }

  /**
   * Show a "default" toast.
   * @param body String or template reference that is shown as the main message.
   * @param header Optional string or template reference that is shown as the header.
   */
  show(body: string | TemplateRef<any>, header?: string | TemplateRef<any>): ActiveToast;

  /**
   * Show a toast.
   */
  show(options: ToastOptions): ActiveToast;
  show(
    optionsOrContent: string | TemplateRef<any> | ToastOptions,
    header?: string | TemplateRef<any>
  ): ActiveToast {
    let toast: ActiveToast;
    let position: 'top' | 'bottom' = 'top';
    let toastId: string | undefined;

    if (this.isToastOptions(optionsOrContent)) {
      toastId = optionsOrContent.toastId;
      toast = {
        actions: optionsOrContent.actions,
        body: optionsOrContent.body,
        delay: optionsOrContent.delay ?? (optionsOrContent.stay ? 0 : this.DEFAULT_TIMEOUT),
        header: optionsOrContent.header,
        hide: () => {
          optionsOrContent.onClose?.();
          this.remove(toast.id);
        },
        hideClose: optionsOrContent.hideClose ?? false,
        hideOnClick: optionsOrContent.hideOnClick ?? true,
        id: toastId ?? ID(),
        showProgress: optionsOrContent.showProgress ?? false,
        stay: optionsOrContent.stay ?? false,
        type: optionsOrContent.type ?? 'default',
      };

      if (optionsOrContent.hideOpenToasts) {
        this.clear();
      }

      if (optionsOrContent.position != null) {
        position = optionsOrContent.position;
      }
    } else {
      toast = {
        actions: [],
        body: optionsOrContent,
        delay: this.DEFAULT_TIMEOUT,
        header,
        hide: () => this.remove(toast.id),
        hideOnClick: true,
        hideClose: false,
        id: ID(),
        showProgress: false,
        stay: false,
        type: 'default',
      };
    }

    if (this.enabled) {
      if (toastId != null) {
        this.updateToast(toastId, toast);
      } else {
        this._toasts$.pipe(first()).subscribe((toasts) => {
          if (position === 'top') {
            this._toasts$.next([toast, ...toasts]);
          } else if (position === 'bottom') {
            this._toasts$.next([...toasts, toast]);
          }
        });
      }
    }

    return toast;
  }

  /**
   * Show a "success" toast.
   * @param body String or template reference that is shown as the main message.
   * @param header Optional string or template reference that is shown as the header.
   */
  success(body: string | TemplateRef<any>, header?: string | TemplateRef<any>): ActiveToast {
    return this.show({
      body,
      header,
      type: 'success',
    });
  }

  updateToast(toastId: string, options: UpdateToastOptions): void {
    this.toasts$.pipe(first()).subscribe((toasts) => {
      const activeToast = toasts.find((activeToast) => activeToast.id === toastId);

      if (activeToast != null) {
        const index = toasts.findIndex((toast) => toast.id === toastId);
        const updatedToast: ActiveToast = {
          ...activeToast,
          actions: options.actions ?? activeToast.actions,
          body: options.body ?? activeToast.body,
          delay: options.delay ?? activeToast.delay,
          header: options.header ?? activeToast.header,
          hideClose: options.hideClose ?? activeToast.hideClose,
          hideOnClick: options.hideOnClick ?? activeToast.hideOnClick,
          showProgress: options.showProgress ?? activeToast.showProgress,
          stay: options.stay ?? activeToast.stay,
          type: options.type ?? activeToast.type,
        };

        const updatedToasts = toasts.toSpliced(index, 1, updatedToast);

        this._toasts$.next(updatedToasts);
      }
    });
  }

  /**
   * Show a "warning" toast.
   * @param body String or template reference that is shown as the main message.
   * @param header Optional string or template reference that is shown as the header.
   */
  warning(body: string | TemplateRef<any>, header?: string | TemplateRef<any>): ActiveToast {
    return this.show({
      body,
      header,
      type: 'warning',
    });
  }

  private isToastOptions(o: any): o is ToastOptions {
    return 'body' in o;
  }
}

/**
 * Available toast types (background-color and color change depending on type).
 */
export type ToastType = 'danger' | 'default' | 'info' | 'success' | 'warning';

export interface ActiveToast {
  /**
   * Actions that will be shown at the bottom of the toast.
   */
  actions: ToastAction[];

  /**
   * String or template reference that is shown as the main content of the toast.
   */
  body: Translatable | TemplateRef<any>;

  /**
   * Delay in ms until the toast is hidden.
   * Set to 0 to disable automatically hiding the toast.
   */
  delay: number;

  /**
   * String or template reference that is shown as the header of the toast.
   */
  header?: Translatable | TemplateRef<any>;

  /**
   * Hide the default close action that appears when `header` is set.
   */
  hideClose: boolean;

  /**
   * Hide this toast when the user clicks on it.
   */
  hideOnClick: boolean;

  /**
   * ID of the toast.
   */
  id: string;

  /**
   * Show a progress indicator (either a spinner (delay = 0) or a progressbar (delay > 0) depending on delay).
   */
  showProgress: boolean;

  /**
   * This toast is not removed when toasts are cleared. It must be hidden manually.
   */
  stay: boolean;

  /**
   * Type of the toast (background-color and color change depending on type).
   */
  type: ToastType;

  /**
   * Hide this toast.
   */
  hide(): void;
}

type ToastActionParam = {
  hide: () => void;
};

export interface ToastAction {
  invoke: (toast: ToastActionParam) => void;
  label: Translatable;
  variant: BootstrapSemanticVariation | 'link';
}

export interface ToastOptions {
  /**
   * Actions that will be shown at the bottom of the toast.
   */
  actions?: ToastAction[];

  /**
   * String or template reference that is shown as the main content of the toast.
   */
  body: Translatable | TemplateRef<any>;

  /**
   * Delay in ms until the toast is hidden.
   * Set to 0 to disable automatically hiding the toast.
   */
  delay?: number;

  /**
   * String or template reference that is shown as the header of the toast.
   */
  header?: Translatable | TemplateRef<any>;

  /**
   * Hide the default close action that appears when `header` is set.
   */
  hideClose?: boolean;

  /**
   * Hide this toast when the user clicks on it.
   */
  hideOnClick?: boolean;

  /**
   * If currently open toasts should be hidden before this toast is opened.
   */
  hideOpenToasts?: boolean;

  /**
   * Position where the toast is shown:
   *  - top: show the new toast before currently open toasts
   *  - bottom: show the new test after currently open toasts
   */
  position?: 'top' | 'bottom';

  /**
   * Show a progress indicator. Type depends on the delay:
   *  - progress bar if the delay is > 0
   *  - spinner if the delay is 0
   */
  showProgress?: boolean;

  /**
   * This toast is not removed when toasts are cleared. It must be hidden manually.
   */
  stay?: boolean;

  /**
   * If a toastId is provided, the toast with that id will be replaced.
   */
  toastId?: string;

  /**
   * Type of the toast (background-color and color change depending on type).
   */
  type?: ToastType;

  /**
   * Function that is called when the user closes the toast manually.
   */
  onClose?(): void;
}

export interface UpdateToastOptions {
  /**
   * Actions that will be shown at the bottom of the toast.
   */
  actions?: ToastAction[];

  /**
   * String or template reference that is shown as the main content of the toast.
   */
  body?: Translatable | TemplateRef<any>;

  /**
   * Delay in ms until the toast is hidden.
   * Set to 0 to disable automatically hiding the toast.
   */
  delay?: number;

  /**
   * String or template reference that is shown as the header of the toast.
   */
  header?: Translatable | TemplateRef<any>;

  /**
   * Hide the default close action that appears when `header` is set.
   */
  hideClose?: boolean;

  /**
   * Hide this toast when the user clicks on it.
   */
  hideOnClick?: boolean;

  /**
   * Show a progress indicator. Type depends on the delay:
   *  - progress bar if the delay is > 0
   *  - spinner if the delay is 0
   */
  showProgress?: boolean;

  /**
   * This toast is not removed when toasts are cleared. It must be hidden manually.
   */
  stay?: boolean;

  /**
   * Type of the toast (background-color and color change depending on type).
   */
  type?: ToastType;
}
